/** Financials: state the facts that decide what a company owes, and see what that decided.
 *
 * **Card C-F, ADR-0069.** `regimes.determine()` had one caller outside tests, the seeder, so a real
 * workspace had no filing obligation at all and Readiness was empty for every customer. This is the
 * screen that was missing: an Admin states an entity's financials for a year (which determines in
 * the same transaction), or asks for a determination outright.
 *
 * **The point of the screen is F-045.** Five yes/no questions sit beside the figures, and none of
 * them has a default. A row made from "turnover is £40m" used to record five answers nobody gave,
 * and the rules read a recorded *no* as a known fact — so SB 253 came out `NOT IN SCOPE`, settled,
 * for a company nobody had asked. So:
 *
 * - The radios start with **neither chosen**, and `buildStatement` puts an answer in the request
 *   only where the person chose one. An untouched question is absent from the body, never `false`.
 * - Submitting with any unanswered is **refused here, before anything is sent**, naming which. The
 *   server refuses it too (`booleans_required`, with the field names), and that is rendered the
 *   same way: a second line of defence is only worth having if it can be seen working.
 * - Every first statement must answer all five. This form asks for all five **every time**,
 *   because there is no read of what is already stated to prefill from, and a form that left a
 *   question blank on the strength of "probably stated before" would be guessing.
 *
 * **Every regulatory sentence here comes from the API.** The status, the reasoning, the missing
 * facts, `in_force_label` ("not yet law", "enforcement enjoined") and the basis for a date all
 * arrive on the response. Nothing below types one, and nothing counts down to a date: a date is
 * shown only for a filing that is owed, beside the sentence that says what it rests on.
 *
 * Admin only (`MAY_STATE`). Everyone else is told who can, and shown no control.
 */

import { ArrowRight, Buildings, CheckCircle, MinusCircle, Question } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import type { FormEvent } from "react";

import { useMarketDefaults } from "../api/config";
import { useRegimes, type Regime } from "../api/regimes";
import { useMe } from "../api/auth";
import { useEntities } from "../api/queries";
import {
  FIGURES,
  buildStatement,
  figureShapeOk,
  isBooleansRequired,
  questionsFor,
  unanswered,
  useDetermine,
  useStateFinancials,
} from "../api/readiness";
import type {
  Answers,
  BooleanFact,
  Determination,
  FigureKey,
  Stated,
} from "../api/readiness";
import { Loaded, WriteFailed } from "../components/Loaded";
import { useFocusRefusal } from "../components/useFocusRefusal";
import { OrganisationLinks } from "./settingsLinks";
import { Panel } from "../components/Panel";
import { EmptyAction, EmptyState, InfoPopover, PageHeaderCard } from "../components/ui";
import { shortDate } from "../lib/format";
import { LABELS, factLabel, question } from "./factLabels";
import { RequireSession } from "../shell/RequireSession";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Screen.module.css";
import own from "./Financials.module.css";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import { useScreen } from "./useScreen";

/** CSS-module class names joined, the undefined ones dropped (`Readiness.tsx` explains why a
 * missing class does not fail to compile). */
function cls(...names: (string | undefined | false)[]): string {
  return names.filter(Boolean).join(" ");
}

const SIZE: readonly FigureKey[] = ["turnover", "balance_sheet_total", "employees"];

/** Which questions and figure groups lead the form, **by the deployment's default regime**
 * (`GET /api/v1/config`, walkthrough C11). The form asked a Californian customer whether it was a
 * "quoted company" before it asked whether it does business in California, because the order was a
 * constant. The order is now **read from the regime's own rules** (`GET /api/v1/regimes`, ADR-0205):
 * the questions that regime's rules read come first, in rule order, then the rest. This is ordering
 * only: every question and group is still shown and still required. What each answer *means* is the
 * rules'. */
function orderedFacts(
  questions: readonly BooleanFact[],
  regime: Regime | undefined,
): readonly BooleanFact[] {
  const lead = (regime?.facts ?? []).filter((fact) => questions.includes(fact));
  return [...lead, ...questions.filter((fact) => !lead.includes(fact))];
}
/** Every fact's unit across the loaded regimes, from `RegimeOut.fact_units` (the rule rows' own). */
function unitsByFact(regimes: readonly Regime[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const regime of regimes) {
    for (const [fact, unit] of Object.entries(regime.fact_units)) {
      if (!out.has(fact)) out.set(fact, unit);
    }
  }
  return out;
}

/** The revenue figures, in dollars: they lead when the default regime's rules read them. */
const REVENUE: readonly FigureKey[] = ["revenue_usd", "prior_year_revenue_usd"];
const GROUP = FIGURES.map((f) => f.key).filter(
  (key) => !SIZE.includes(key) && !REVENUE.includes(key),
);

const STATUS: Record<string, string> = {
  required: "Must file",
  satisfied_by_parent: "Parent files",
  exempt: "Exempt",
  not_in_scope: "Not in scope",
};

/** One position, as a determination has just produced it.
 *
 * **`undetermined` overrides the label.** "Not in scope" beside a missing figure that could have
 * changed the answer is the most confident sentence the product can get wrong, so the pill says
 * *Needs figures* and the missing facts are named beneath it — they are the next action.
 */
/** One rule behind a position, as Readiness's drawer reads it: the sentence, the paragraph that
 * says it, and a dispute where the verification log records one (C-02a). Every word is the row's.
 * The mark is never only a colour - the word goes to a screen reader. */
function WhyRow({ reason }: { reason: Determination["reasoning"][number] }) {
  const word = reason.missing ? "Fact missing" : reason.satisfied === "yes" ? "Met" : "Not met";
  const Mark = reason.missing ? Question : reason.satisfied === "yes" ? CheckCircle : MinusCircle;
  return (
    <li className={cls(own.whyRow)} data-testid="position-why-row" data-word={word}>
      <Mark size={14} weight={reason.satisfied === "yes" ? "fill" : "regular"} aria-hidden="true" />
      <span className="visuallyHidden">{word}: </span>
      <span>
        {reason.statement}{" "}
        <span className={cls(own.cite)}>
          {reason.source_url ? (
            <a href={reason.source_url} target="_blank" rel="noreferrer">
              {reason.citation}
            </a>
          ) : (
            reason.citation
          )}
        </span>
        {reason.dispute ? (
          <span className={cls(own.whyDispute)} data-testid="position-why-dispute">
            {reason.dispute}
          </span>
        ) : null}
      </span>
    </li>
  );
}

/** Where a filing that is owed is opened. The same search Readiness's row carries, so the Reports
 * screen resolves the same filing, window and Scope 2 method. */
interface FilingLink {
  method: "location" | "market";
  from: string | undefined;
  to: string | undefined;
}

function Position({ row, link }: { row: Determination; link: FilingLink }) {
  const label = row.undetermined ? "Needs figures" : (STATUS[row.status] ?? row.status);
  return (
    <li
      className={cls(own.position)}
      data-testid="determination"
      data-regime={row.regime_key}
      data-status={row.status}
      data-undetermined={String(row.undetermined)}
    >
      <span className={cls(own.line)}>
        <span className={cls(own.name)}>{row.regime_name}</span>
        <span
          className={cls(
            own.pill,
            row.undetermined ? own.pillUnknown : row.status === "required" ? own.pillRequired : undefined,
          )}
        >
          {label}
        </span>
        {/* The row's own words — "not yet law", "enforcement enjoined" — verbatim. */}
        {row.in_force_label ? (
          <span className={cls(own.flag)} data-testid="in-force-label">
            {row.in_force_label}
          </span>
        ) : null}
      </span>
      <span className={cls(own.sub)}>
        {row.legal_entity_name} · year ended {shortDate(row.financial_year_end)} · rules v
        {row.regime_version}
      </span>
      {row.in_force_caveat ? (
        <span className={cls(own.cite)} data-testid="in-force-caveat">
          {row.in_force_caveat}
        </span>
      ) : null}
      {row.missing_facts.length > 0 ? (
        <span className={cls(own.needs)} data-testid="missing-facts">
          {row.undetermined
            ? "Needs before we can say: "
            : "Not recorded, and it did not change the answer: "}
          {row.missing_facts.map(factLabel).join(", ")}
        </span>
      ) : null}
      {/* A date only for a filing that is owed, and never without the sentence behind it. */}
      {row.status === "required" && !row.undetermined ? (
        <span className={cls(own.cite)} data-testid="due" data-dated={String(Boolean(row.due_date))}>
          <strong>{row.due_date ? `Due ${shortDate(row.due_date)}` : "No date set"}</strong>{" "}
          {row.due_date_basis}
        </span>
      ) : null}
      {/* **Why, then what is outstanding.** A position with no reasoning said *what* and not *why*,
          and a required filing was a dead end: the checklist of what is outstanding lives on the
          filing's own screen. Both are the rows' words and a link, nothing typed here. */}
      {row.reasoning.length > 0 ? (
        <details className={cls(own.why)} data-testid="position-why">
          <summary>
            Why: {row.reasoning.length} check{row.reasoning.length === 1 ? "" : "s"}, rules version{" "}
            {row.regime_version}
          </summary>
          <ul className={cls(own.whyList)}>
            {row.reasoning.map((reason, index) => (
              <WhyRow key={`${reason.rule}-${index}`} reason={reason} />
            ))}
          </ul>
        </details>
      ) : null}
      {row.status === "required" && !row.undetermined ? (
        <Link
          to="/reports"
          search={{ method: link.method, from: link.from, to: link.to, filing: row.obligation_id }}
          className={cls(own.open)}
          data-testid="position-open-filing"
          aria-label={`See what is outstanding on this filing: ${row.regime_name}, ${row.legal_entity_name}`}
        >
          See what is outstanding
          <ArrowRight size={12} weight="bold" aria-hidden="true" />
        </Link>
      ) : null}
      {row.supersedes_obligation_id ? (
        <span className={cls(own.cite)}>
          Sits beside an older determination for the same year, made under earlier rules. The older
          one is kept.
        </span>
      ) : null}
    </li>
  );
}

function Positions({ rows }: { rows: readonly Determination[] }) {
  const screen = useScreen();
  const link: FilingLink = {
    method: screen.method,
    from: screen.window?.period_start,
    to: screen.window?.period_end,
  };
  // An empty list is never rendered as an empty list: on this screen it would read as *nothing is
  // owed*, which is what the determination exists not to say when it has not looked.
  if (rows.length === 0) {
    return (
      <p className={cls(own.refused)} role="alert" data-testid="no-positions">
        The determination returned no positions. That is not the same as nothing being owed.
      </p>
    );
  }
  return (
    <ul className={cls(own.positions)} data-testid="determined">
      {rows.map((row) => (
        <Position key={row.obligation_id} row={row} link={link} />
      ))}
    </ul>
  );
}

/** The questions nobody answered, named — whether the form or the server found them. */
function Unanswered({ facts, source }: { facts: readonly string[]; source: "form" | "server" }) {
  return (
    <div className={cls(own.refused)} role="alert" data-testid="booleans-required" data-source={source}>
      <strong>Every yes/no question needs an answer.</strong> A question left blank is not recorded
      as “no”, and nothing was saved. Still to answer:
      <ul>
        {facts.map((fact) => (
          <li key={fact} data-testid={`unanswered-${fact}`}>
            {factLabel(fact)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function figureInput(
  key: FigureKey,
  figures: Partial<Record<FigureKey, string>>,
  setFigure: (key: FigureKey, value: string) => void,
  errors: Partial<Record<FigureKey, string>>,
) {
  const field = FIGURES.find((f) => f.key === key);
  const error = errors[key];
  return (
    <label className={cls(own.field)} key={key}>
      {LABELS[key]}
      <input
        type="text"
        inputMode={field?.kind === "count" ? "numeric" : "decimal"}
        autoComplete="off"
        value={figures[key] ?? ""}
        aria-invalid={error ? true : undefined}
        data-testid={`figure-${key}`}
        onChange={(event) => setFigure(key, event.target.value)}
      />
      {error ? (
        <span className={cls(own.fieldError)} data-testid={`figure-error-${key}`}>
          {error}
        </span>
      ) : null}
    </label>
  );
}

function shapeMessage(key: FigureKey): string {
  const kind = FIGURES.find((f) => f.key === key)?.kind;
  return kind === "count"
    ? "Whole numbers only."
    : kind === "energy"
      ? "Digits only, up to three decimal places."
      : "Digits only, up to two decimal places.";
}

function StatementForm({ initialYear }: { initialYear: string }) {
  const entities = useEntities();
  const state = useStateFinancials();
  const [entityId, setEntityId] = useState("");
  const [year, setYear] = useState(initialYear);
  const [answers, setAnswers] = useState<Answers>({});
  const market = useMarketDefaults();
  // **Pre-filled from the one market default and visible**, not blank free text (walkthrough C11).
  // `null` is "the person has not typed", so the field follows the default once it has loaded.
  const [typedCurrency, setCurrency] = useState<string | null>(null);
  // The regimes' own questions and the default regime's rule order, both from the API.
  const regimes = useRegimes();
  // **What each figure is compared in are the rules' own** (`RegimeOut.fact_units`). The
  // Currency field belongs to the size figures, so it is pre-filled only with a currency the rules
  // compare size in: a US deployment's USD default would otherwise be refused by SECR's pounds.
  const comparedIn = unitsByFact(regimes.data ?? []);
  const sizeCurrencies = new Set(
    FIGURES.filter((f) => f.kind === "money").flatMap((f) => comparedIn.get(f.key) ?? []),
  );
  const marketCurrency = market?.currency ?? "";
  const currency = typedCurrency ?? (sizeCurrencies.has(marketCurrency) ? marketCurrency : "");
  const revenueUnit = comparedIn.get("revenue_usd");
  const defaultRegime = regimes.data?.find((entry) => entry.key === market?.regime_key);
  const questions = questionsFor((regimes.data ?? []).flatMap((entry) => entry.extra_facts));
  const revenueFirst = defaultRegime?.facts.includes("revenue_usd") === true;
  const [figures, setFigures] = useState<Partial<Record<FigureKey, string>>>({});
  /** What the form itself refused, before sending. Cleared per question as it is answered. */
  const [blank, setBlank] = useState<BooleanFact[]>([]);
  const [problems, setProblems] = useState<{
    entity?: string;
    year?: string;
    figures: Partial<Record<FigureKey, string>>;
  }>({ figures: {} });
  const [done, setDone] = useState<Stated | null>(null);

  // The one company, when there is exactly one, is chosen; with more, nothing is assumed.
  const only = entities.data?.length === 1 ? entities.data[0]?.id : undefined;
  const chosen = entityId || only || "";

  const serverUnanswered = isBooleansRequired(state.error) ? state.error.fields : [];
  const flagged = new Set<string>([...blank, ...serverUnanswered]);

  // After the form refuses (an unanswered question, a malformed figure) or the server does, focus goes
  // to the first marked field or its sentence, not the button that was pressed (G-P8-45). The token
  // is whichever refusal is current: each submit builds new `blank` and `problems` objects.
  const [formEl, setFormEl] = useState<HTMLFormElement | null>(null);
  const hasProblem = Boolean(problems.entity ?? problems.year) || Object.keys(problems.figures).length > 0;
  useFocusRefusal(formEl, state.error ?? (hasProblem ? problems : blank.length > 0 ? blank : null));

  function answer(fact: BooleanFact, value: boolean) {
    setAnswers((current) => ({ ...current, [fact]: value }));
    setBlank((current) => current.filter((f) => f !== fact));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    state.reset();
    setDone(null);

    const numeric = Number(year);
    const next: typeof problems = { figures: {} };
    if (!chosen) next.entity = "Choose the company these figures are for.";
    if (!/^\d{4}$/.test(year) || numeric < 1990 || numeric > 2100) {
      next.year = "Enter the financial year as four digits, for example 2025.";
    }
    for (const field of FIGURES) {
      const text = figures[field.key]?.trim();
      if (text && !figureShapeOk(field.kind, text)) next.figures[field.key] = shapeMessage(field.key);
    }
    const missing = unanswered(answers, questions);
    setBlank(missing);
    setProblems(next);
    // **Nothing is sent** while any question is unanswered, any figure is malformed, or the
    // company or year is missing. The refusal is shown here, and the server's own would only
    // repeat it.
    if (missing.length > 0 || next.entity || next.year || Object.keys(next.figures).length > 0) {
      return;
    }

    state.mutate(
      { entityId: chosen, year: numeric, body: buildStatement({ answers, currency, figures }) },
      { onSuccess: (result) => setDone(result) },
    );
  }

  const showBlank: readonly string[] = blank.length > 0 ? blank : serverUnanswered;

  const sizeGroup = (
      <fieldset className={cls(own.group)}>
        <span className={cls(own.groupTitle)}>Size</span>
        <span className={cls(own.fieldRow)}>
          <label className={cls(own.field)}>
            Currency
            <input
              type="text"
              maxLength={3}
              autoComplete="off"
              value={currency}
              data-testid="financials-currency"
              onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            />
            <span className={cls(own.hint)} data-testid="financials-currency-hint">
              {sizeCurrencies.size > 0
                ? `Three letters, size figures only; rules compare in ${[...sizeCurrencies].sort().join(", ")}.`
                : "Three letters, for size figures only."}
            </span>
          </label>
          {SIZE.map((key) => figureInput(key, figures, (k, v) => setFigures((c) => ({ ...c, [k]: v })), problems.figures))}
        </span>
      </fieldset>
  );

  const revenueGroup = (
      <fieldset className={cls(own.group)}>
        <span className={cls(own.groupTitle)}>Revenue figures</span>
        {revenueUnit ? (
          <span className={cls(own.hint)} data-testid="financials-revenue-unit">
            Stated in {revenueUnit}
          </span>
        ) : null}
        <span className={cls(own.fieldRow)}>
          {REVENUE.map((key) => figureInput(key, figures, (k, v) => setFigures((c) => ({ ...c, [k]: v })), problems.figures))}
        </span>
      </fieldset>
  );

  return (
    <form ref={setFormEl} className={cls(own.form)} onSubmit={submit} noValidate data-testid="financials-form">
      <Loaded
        query={entities}
        of="This workspace's companies"
        empty={
          <>
            This workspace has no company yet, so there is nothing to state figures for. Add a
            company on the{" "}
            <Link to="/organisation" search={{ method: "location" }} className={styles.fixLink}>
              Organisation
            </Link>{" "}
            screen first.
          </>
        }
      >
        {(rows) => (
          <span className={cls(own.fieldRow)}>
            <label className={cls(own.field)}>
              Company
              <select
                value={chosen}
                aria-invalid={problems.entity ? true : undefined}
                data-testid="financials-entity"
                onChange={(event) => setEntityId(event.target.value)}
              >
                <option value="">Choose a company</option>
                {rows.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.legal_name} · {row.jurisdiction}
                  </option>
                ))}
              </select>
              {problems.entity ? (
                <span className={cls(own.fieldError)} data-testid="entity-error">
                  {problems.entity}
                </span>
              ) : null}
            </label>
            <label className={cls(own.field)}>
              Financial year
              <input
                type="text"
                inputMode="numeric"
                value={year}
                aria-invalid={problems.year ? true : undefined}
                data-testid="financials-year"
                onChange={(event) => setYear(event.target.value)}
              />
              {problems.year ? (
                <span className={cls(own.fieldError)} data-testid="year-error">
                  {problems.year}
                </span>
              ) : null}
              <span className={cls(own.hint)}>
                The calendar year the company&rsquo;s own year end falls in.
              </span>
            </label>
          </span>
        )}
      </Loaded>

      <Loaded
        query={regimes}
        of="The questions the regimes ask"
        empty="No regime is loaded, so there is nothing to answer yet. Load the reference data first."
      >
        {() => (
        <fieldset className={cls(own.facts)} data-testid="financials-facts">
          <legend>Answer every question</legend>
          <span className={cls(own.hint)}>
            None of these is answered for you. A question you leave alone is not saved as
            &ldquo;no&rdquo;; it stops the statement.
          </span>
          {orderedFacts(questions, defaultRegime).map((fact) => (
            <fieldset
              key={fact}
              className={cls(own.question)}
              aria-invalid={flagged.has(fact) ? true : undefined}
              data-testid={`fact-${fact}`}
            >
              <legend>{question(fact)}</legend>
              <span className={cls(own.choices)}>
                <label>
                  <input
                    type="radio"
                    name={fact}
                    checked={answers[fact] === true}
                    data-testid={`fact-${fact}-yes`}
                    onChange={() => answer(fact, true)}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name={fact}
                    checked={answers[fact] === false}
                    data-testid={`fact-${fact}-no`}
                    onChange={() => answer(fact, false)}
                  />
                  No
                </label>
              </span>
            </fieldset>
          ))}
        </fieldset>
        )}
      </Loaded>

      {showBlank.length > 0 ? (
        <Unanswered facts={showBlank} source={blank.length > 0 ? "form" : "server"} />
      ) : null}

      {revenueFirst ? <>{revenueGroup}{sizeGroup}</> : <>{sizeGroup}{revenueGroup}</>}

      <details>
        <summary className={cls(own.groupTitle)}>Group and energy figures</summary>
        <span className={cls(own.fieldRow)}>
          {GROUP.map((key) => figureInput(key, figures, (k, v) => setFigures((c) => ({ ...c, [k]: v })), problems.figures))}
        </span>
      </details>

      <span className={cls(own.hint)}>
        A blank figure is not stated; nothing already stated is cleared.
      </span>

      {/* The server's refusal, in the server's words. A missing-answers refusal is shown above,
          beside the questions it is about, so it is not repeated here. */}
      {isBooleansRequired(state.error) ? null : (
        <WriteFailed error={state.error} of="that statement" />
      )}

      <span>
        <button
          type="submit"
          className={styles.primary}
          disabled={state.isPending || !regimes.data}
          data-testid="financials-submit"
        >
          {state.isPending ? "Saving…" : "State these figures"}
        </button>
      </span>

      {done ? (
        <div className={cls(own.result)} data-testid="statement-result" data-unchanged={String(done.unchanged)}>
          {done.unchanged ? (
            <p className={styles.note} data-testid="statement-unchanged">
              Nothing changed, so nothing was saved and nothing was determined again. Run a
              determination if you want the current rules applied.
            </p>
          ) : (
            <>
              <p className={styles.note} data-testid="statement-saved">
                Saved for the year ended {shortDate(done.financial_year_end)}
                {done.first_statement ? " — the first statement for this year" : ""}. Positions
                after the statement:
              </p>
              <Positions rows={done.determinations} />
            </>
          )}
        </div>
      ) : null}
    </form>
  );
}

function DetermineForm({ initialYear }: { initialYear: string }) {
  const determine = useDetermine();
  const [year, setYear] = useState(initialYear);
  const [problem, setProblem] = useState<string | null>(null);

  function run(event: FormEvent) {
    event.preventDefault();
    const numeric = Number(year);
    if (!/^\d{4}$/.test(year) || numeric < 1990 || numeric > 2100) {
      setProblem("Enter the year as four digits, for example 2025.");
      return;
    }
    setProblem(null);
    determine.reset();
    determine.mutate({ year: numeric });
  }

  return (
    <form className={cls(own.form)} onSubmit={run} noValidate data-testid="determine-form">
      <span className={cls(own.fieldRow)}>
        <label className={cls(own.field)}>
          Financial year
          <input
            type="text"
            inputMode="numeric"
            value={year}
            aria-invalid={problem ? true : undefined}
            data-testid="determine-year"
            onChange={(event) => setYear(event.target.value)}
          />
          {problem ? (
            <span className={cls(own.fieldError)} data-testid="determine-year-error">
              {problem}
            </span>
          ) : null}
        </label>
      </span>
      <WriteFailed error={determine.error} of="that determination" retrySafe />
      <span>
        <button
          type="submit"
          className={styles.secondary}
          disabled={determine.isPending}
          data-testid="determine-submit"
        >
          {determine.isPending ? "Determining…" : "Run determination"}
        </button>
      </span>
      {determine.data ? (
        <div className={cls(own.result)} data-testid="determine-result">
          <p className={styles.note}>Positions for {determine.data.year}:</p>
          <Positions rows={determine.data.determinations} />
        </div>
      ) : null}
    </form>
  );
}

function FinancialsScreenBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  // The year the figures are for, offered from the period in force. A suggestion the person can
  // change and sees: the field is never filled with a year they did not see.
  const suggested = screen.window?.period_end.slice(0, 4) ?? "";
  const entities = useEntities();

  // **No company: nothing to state and nothing to determine** (KD, 2026-11-02). Two forms whose every
  // field and whose button could only fail were a brand-new workspace's first look at this screen; the
  // one thing to do is add the company, and that is the one control.
  if (isAdmin && entities.data !== undefined && entities.data.length === 0) {
    return (
      <ScreenFrame screen={screen} title="Financials" crumb="the facts that decide what is owed">
        <PageHeaderCard
          icon={Buildings}
          title="Financials"
          actions={<InfoPopover infoKey="legacy.financials-page" />}
        />
        <OrganisationLinks current="/financials" search={screen} />
        <Panel title="Financials" column="1 / 13">
          <EmptyState
            icon={Buildings}
            title="No company yet"
            compact
            testId="financials-no-company"
            action={
              <EmptyAction
                label="Add a company"
                allowed
                to="/facilities"
                search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "companies" })}
                testId="financials-add-company"
              />
            }
          >
            Financials are stated for a company.
          </EmptyState>
        </Panel>
      </ScreenFrame>
    );
  }

  if (!isAdmin) {
    return (
      <ScreenFrame screen={screen} title="Financials" crumb="the facts that decide what is owed">
        <Panel title="Financials" column="1 / 8">
          <p className={styles.note} data-testid="financials-who-can">
            An <strong>Admin</strong> states a company&rsquo;s financials and runs determination.
            These figures decide whether a filing is owed, so the person who approves a report is
            not the one who states them. You can read the result on{" "}
            <Link to="/" search={{ method: screen.method }} className={styles.fixLink}>
              Readiness
            </Link>
            .
          </p>
        </Panel>
      </ScreenFrame>
    );
  }

  return (
    <ScreenFrame screen={screen} title="Financials" crumb="the facts that decide what is owed" bodyScrolls>
      <PageHeaderCard
          icon={Buildings}
          title="Financials"
          actions={<InfoPopover infoKey="legacy.financials-page" />}
        />
      <OrganisationLinks current="/financials" search={screen} />
      <Panel
        title="State a company's financials"
        column="1 / 8"
        action={<InfoPopover infoKey="legacy.financials-state" />}
      >
        <StatementForm initialYear={suggested} />
      </Panel>
      <Panel
        title="Run a determination"
        column="8 / 13"
        action={<InfoPopover infoKey="legacy.financials-determine" />}
      >
        <DetermineForm initialYear={suggested} />
      </Panel>
    </ScreenFrame>
  );
}

export function FinancialsScreen() {
  return (
    <RequireSession>
      <FinancialsScreenBody />
    </RequireSession>
  );
}
